/**
 * Extended page model from DCA (Digital Content Assembly) including Adobe Target mbox response data.
 * Used to determine A/B test experiment assignment (e.g., convoAI conversational search variant).
 */
export interface IUpdatedPageModel extends Record<string, unknown> {
  alloyModelJSON: Record<string, Record<string, unknown>>;
  /** Mbox components from Adobe Target UXL, containing experience assignment and metadata */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mboxData?: Array<{
    mboxName: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uxlResponse?: {
      isTargetContentError?: boolean;
      metaData?: {
        experience?: string;
      };
      responseTokens?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [key: string]: any;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  }>;
}

/**
 * Determines if conversational search should be enabled based on Adobe Target (UXL) response.
 * Looks for the 'target-convoAI-mbox' component in the page model's mbox data.
 * User must be assigned to the convoAI experience variant to enable conversational search.
 * @param updatedPageModel - DCA page model containing mbox/UXL response data
 * @returns true if user is in the convoAI experiment variant, false otherwise
 */
export const enableConversationalSearch = (updatedPageModel: IUpdatedPageModel | undefined) => {
  let enableConvoAI = false;
  let activityName;
  let experienceName;
  if (updatedPageModel?.mboxData) {
    let hasError = false;
    let searchExperience;
    for (let componentCount = 0; componentCount < updatedPageModel.mboxData.length; componentCount++) {
      const component = updatedPageModel.mboxData[componentCount];
      if (component.mboxName === 'target-convoAI-mbox') {
        // Check if UXL processing is complete (has response or error)
        if (component?.uxlResponse) {
          if (component?.uxlResponse?.isTargetContentError === true) {
            hasError = true;
          } else {
            // 'convoAI' = user assigned to conversational search experiment variant
            searchExperience = component?.uxlResponse?.metaData?.experience;
            // Only extract activity/experience names when user has an experiment variant assigned
            if (component?.uxlResponse?.metaData?.experience) {
              activityName = component?.uxlResponse?.responseTokens?.['activity.name'];
              experienceName = component?.uxlResponse?.responseTokens?.['experience.name'];
            }
          }
        }
        break;
      }
    }

    if (hasError) {
      // On Target/UXL error, default to control (no conversational search)
      enableConvoAI = false;
    } else if (searchExperience === 'convoAI') {
      // User is in the convoAI variant - enable conversational search
      enableConvoAI = true;
    }
  }
  return {
    enableConvoAI,
    activityName,
    experienceName,
  };
};
